import {
    isActivePreApprovalSubmission,
    isSynchronyProvider,
    type ActivePreApprovalSubmissionSetupData,
} from './preApprovalGate';

/** Setup-data fields needed to decide soft vs hard application phase. */
export interface ApplicationPhaseSetupData extends ActivePreApprovalSubmissionSetupData {
    isApplyingForPrequalification?: boolean;
    hasTransitionedToHardApp?: boolean;
}

export interface ApplicationPhaseOptions {
    /**
     * When true, treat the session as a financing-API soft prequal even if setup data does not
     * set `isApplyingForPrequalification` (Service Finance CP2 when the prequals flag is on).
     */
    treatAsSoftPrequal?: boolean;
}

const isServiceFinanceProvider = (name?: string | null): boolean =>
    name?.toLowerCase() === 'servicefinance';

/**
 * True when the current session is applying against a pre-approval offer (GreenSky plan-card
 * conversion, or Synchrony banner conversion). Not the same as `isActivePreApprovalSubmission`,
 * which is GreenSky-only and gates the financing-direct submit path.
 */
export const isPreApprovalApplication = (setupData?: ApplicationPhaseSetupData | null): boolean => {
    if (!setupData?.preApprovalId) {
        return false;
    }
    if (isSynchronyProvider(setupData.provider)) {
        return true;
    }
    return isActivePreApprovalSubmission(setupData);
};

/**
 * True when setup data describes the soft (prequalification) phase of an application.
 * Pre-approval conversions, continue-to-hard, and existing loans are hard.
 */
export const isSoftApplicationPhase = (
    setupData?: ApplicationPhaseSetupData | null,
    options?: ApplicationPhaseOptions,
): boolean => {
    if (!setupData || isPreApprovalApplication(setupData)) {
        return false;
    }
    if (setupData.hasTransitionedToHardApp || setupData.loanId) {
        return false;
    }
    if (options?.treatAsSoftPrequal && isServiceFinanceProvider(setupData.provider)) {
        return true;
    }
    if (setupData.isApplyingForPrequalification === true) {
        return true;
    }
    return !!setupData.prequalificationId;
};

/** Inverse of `isSoftApplicationPhase`, including pre-approval conversions. */
export const isHardApplicationPhase = (
    setupData?: ApplicationPhaseSetupData | null,
    options?: ApplicationPhaseOptions,
): boolean => !isSoftApplicationPhase(setupData, options);
